import React, { useMemo, useRef, useState } from "react";
import { FaCompress, FaExpand, FaHome, FaLock, FaUnlock } from "react-icons/fa";
import styles from "./style.module.scss";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  LegendItem,
  ChartDataset,
  PointStyle,
  TooltipItem,
  Scale,
  ChartEvent,
  ActiveElement,
} from "chart.js";
import zoomPlugin from "chartjs-plugin-zoom";
import ChartDataLabels from "chartjs-plugin-datalabels";
import annotationPlugin from "chartjs-plugin-annotation";
import {
  AxisSpec,
  MetricLap,
  PlotSpec,
} from "../../../types/performanceMetrics";
import { useDispatch } from "react-redux/es/hooks/useDispatch";
import { disablePoint } from "../../../state/performanceMetricsReducer";

ChartJS.register(
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  zoomPlugin,
  ChartDataLabels,
  annotationPlugin,
);

ChartJS.defaults.font.family = "JaguarExuberant, Helvetica, Arial, sans-serif";

interface MetricRowProps {
  axisSpec: AxisSpec;
  plotSpecs: PlotSpec[];
  metricLaps: MetricLap[];
  isMaximised?: boolean;
  onMaximise?: () => void;
  onMinimise?: () => void;
  scrollTargetRef?: React.RefObject<HTMLElement>;
}

const markerMap = {
  v: "rectRot",
  "^": "triangle",
  o: "circle",
};

const lineStyleMap = {
  "-": [],
  "--": [5, 5],
  ":": [2, 2],
  "-.": [5, 2, 2, 2],
};

function translateColour(colour: string | number[]) {
  if (typeof colour === "string") {
    if (colour.startsWith("#")) {
      return colour;
    } else if (colour.length === 1) {
      const colourMap: Record<string, string> = {
        r: "red",
        g: "green",
        b: "blue",
      };
      return colourMap[colour] || "black";
    } else {
      return colour;
    }
  } else if (Array.isArray(colour) && colour.length === 3) {
    const [r, g, b] = colour.map((c) => Math.round(c * 255));
    return `rgb(${r}, ${g}, ${b})`;
  }
  return "black";
}

function MetricRow({
  axisSpec,
  plotSpecs,
  metricLaps,
  isMaximised = false,
  onMaximise,
  onMinimise,
  scrollTargetRef,
}: MetricRowProps) {
  const dispatch = useDispatch();
  const chartRef = useRef<ChartJS<"line">>(null);
  const isLockedRef = useRef(true);
  const [isLocked, setIsLocked] = useState(true);

  // Store current zoom state to persist across data changes
  const currentZoomRef = useRef<{
    xMin: number;
    xMax: number;
    yMin: number;
    yMax: number;
  } | null>(null);

  const chartData = useMemo(() => {
    let metric = "";

    const lapsByDatasetIndex: { [key: number]: MetricLap[] } = {};

    if (!metricLaps || metricLaps.length === 0) {
      return {
        datasets: [],
        lapsByDatasetIndex,
        axisLimits: { xMin: 0, xMax: 1, yMin: 0, yMax: 1 },
      };
    }

    // Create the scatter datasets from each lap
    const runDatasets: ChartDataset<"line">[] = [];

    let datasetMin = Infinity;
    let datasetMax = -Infinity;
    plotSpecs.forEach((plotSpec: PlotSpec) => {
      if (plotSpec.Axis !== axisSpec.Axis) {
        return;
      }
      metric = plotSpec.MetricName || "";
      let currentRun: { x: number; y: number }[] = [];
      let currentLaps: MetricLap[] = [];
      metricLaps.forEach((metricLap: MetricLap, i: number) => {
        switch (metricLap.name?.[0]) {
          case "RunBreak": {
            if (currentRun.length > 0) {
              runDatasets.push({
                data: currentRun,
                pointBorderColor: translateColour(
                  plotSpec.MarkerEdgeColour || "#000000ff",
                ),
                pointBackgroundColor: translateColour(
                  plotSpec.MarkerFillColour || "#000000ff",
                ),
                borderColor: translateColour(plotSpec.Colour || "#000000ff"),
                borderWidth: plotSpec.LineWidth || 1,
                borderDash:
                  lineStyleMap[
                    plotSpec.LineStyle as keyof typeof lineStyleMap
                  ] || [],
                pointStyle:
                  markerMap[plotSpec.MarkerStyle as keyof typeof markerMap] ||
                  "circle",
                label: plotSpec.MetricName || "",
                datalabels: {
                  display: false,
                },
              });
              currentRun = [];
              lapsByDatasetIndex[runDatasets.length - 1] = currentLaps;
              currentLaps = [];
            }
            break;
          }
          default: {
            let metricValue;
            if (metric === "Lap Time") {
              metricValue = metricLap.laptime;
            } else {
              metricValue =
                metricLap.metrics?.[metric as keyof typeof metricLap.metrics];
            }
            // Fuck ass Matlab sending null as a []
            if (typeof metricValue === "number" && !isNaN(metricValue)) {
              currentRun.push({
                x: i,
                y: metricValue,
              });
              datasetMin = Math.min(datasetMin, metricValue);
              datasetMax = Math.max(datasetMax, metricValue);
              currentLaps.push(metricLap);
            }
            break;
          }
        }
      });
    });

    // Figure out x/y mins and maxes based on the data, with some padding
    // The type of check is for when "Auto" is sent instead of null for auto axis
    const yRange = datasetMax - datasetMin;
    const xRange = metricLaps.length;
    const xMin = 0 - xRange / 1000;
    const xMax = xRange - 1 + xRange / 1000;
    const yMin =
      typeof axisSpec.ChannelYMin === "number"
        ? axisSpec.ChannelYMin
        : Math.floor(datasetMin - 0.1 * yRange);
    const yMax =
      typeof axisSpec.ChannelYMax === "number"
        ? axisSpec.ChannelYMax
        : Math.ceil(datasetMax + 0.1 * yRange);

    // Add high power points as separate dataset
    const highPowerDataset: ChartDataset<"line">[] = [];
    metricLaps.forEach((metricLap: MetricLap, i: number) => {
      if (metricLap.BHPMode === 1 && metricLap.name?.[0] !== "RunBreak") {
        highPowerDataset.push({
          data: [
            { x: i, y: -Number.MAX_VALUE },
            { x: i, y: Number.MAX_VALUE },
          ],
          borderColor: "rgba(0, 200, 255, 0.2)",
          borderWidth: 10,
          pointRadius: 0,
          datalabels: {
            display: false,
          },
        });
      }
    });

    // Add run break indicators as separate datasets
    const emptyRunBreakDataSet: ChartDataset<"line">[] = [];
    const runBreakAnnotations: {
      colour: string;
      x: number;
      label: string;
      labelColor: string;
      smallFont: boolean;
      xAdjust: number;
      type: "run" | "driver";
    }[] = [];

    let lastLayer = 1;
    metricLaps.forEach((metricLap: MetricLap, i: number) => {
      let lastX;

      // When layer increments add solid vertical line using the colour of the previous driver
      if (runBreakAnnotations.length > 0 && metricLap.Layer !== lastLayer) {
        const lastMetricLap = metricLaps[i - 1];
        if (lastMetricLap) {
          runBreakAnnotations[runBreakAnnotations.length - 1].colour =
            translateColour(lastMetricLap.Colour || "#000000ff");
          runBreakAnnotations[runBreakAnnotations.length - 1].type = "driver";
          runBreakAnnotations[runBreakAnnotations.length - 1].labelColor =
            "#000000";
          runBreakAnnotations[runBreakAnnotations.length - 1].smallFont = true;
          lastX = runBreakAnnotations[runBreakAnnotations.length - 1].x;
          runBreakAnnotations[runBreakAnnotations.length - 1].x -=
            xRange / 1000;
        }

        // And a solid line for the new driver layer
        if (lastX !== undefined) {
          runBreakAnnotations.push({
            x: lastX + xRange / 1000,
            colour: translateColour(metricLap.Colour || "#000000ff"),
            label: metricLap.driver || "",
            labelColor: translateColour(metricLap.Colour || "#000000ff"),
            smallFont: false,
            xAdjust: 0,
            type: "driver",
          });
          emptyRunBreakDataSet.push({
            data: [{ x: lastX, y: 0 }],
            borderColor: "transparent",
            backgroundColor: "transparent",
            pointRadius: 0,
            pointHoverRadius: 0,
            datalabels: { display: false },
          });
        }
      }

      lastLayer = metricLap.Layer || lastLayer;

      // For each run break add a dashed vertical line
      if (metricLap.name?.[0] === "RunBreak") {
        // If not first/last break then initially coloured grey, but overridden if last break before layer increment
        runBreakAnnotations.push({
          x: i,
          colour:
            i === 0 || i === xRange - 1
              ? translateColour(metricLap.Colour || "#000000ff")
              : "#7c7c7cff",
          label:
            i === 0 ? metricLap.driver || "" : metricLap.description?.[0] || "",
          labelColor:
            i === 0
              ? translateColour(metricLap.Colour || "#000000ff")
              : "#000000",
          smallFont: i !== 0,
          xAdjust: i === 0 ? -18 : i === xRange - 1 ? 14 : 0,
          type: i === 0 || i === xRange - 1 ? "driver" : "run",
        });
        emptyRunBreakDataSet.push({
          data: [{ x: i, y: 0 }],
          borderColor: "transparent",
          backgroundColor: "transparent",
          pointRadius: 0,
          pointHoverRadius: 0,
          datalabels: { display: false },
        });
      }
    });

    // Add reference lines as separate datasets
    const lineDatasets: ChartDataset<"line">[] = [];
    const referenceLineConfigs = [
      { value: axisSpec.RefLine, color: "#bebebeff" },
      { value: axisSpec.Warning, color: "#ff8400ff" },
      { value: axisSpec.Critical, color: "rgba(255, 0, 0, 1)" },
    ];

    referenceLineConfigs.forEach(({ value, color }) => {
      if (typeof value === "number" && !isNaN(value)) {
        lineDatasets.push({
          data: [
            { x: 0, y: value },
            { x: xRange - 1, y: value },
          ],
          borderColor: color,
          borderDash: [5, 5],
          borderWidth: 1,
          pointRadius: 0,
          datalabels: {
            display: false,
          },
        });
      }
    });

    // Add lap time line seperately as not drawn from metrics
    if (metric === "Lap Time") {
      lineDatasets.push({
        data: [
          { x: 0, y: datasetMin },
          { x: xRange - 1, y: datasetMin },
        ],
        borderColor: "#ff0099ff",
        borderDash: [5, 5],
        borderWidth: 1,
        pointRadius: 0,
        datalabels: {
          display: false,
        },
      });
    }

    // Combine all datasets
    const datasets = [
      ...highPowerDataset,
      ...runDatasets,
      ...emptyRunBreakDataSet,
      ...lineDatasets,
    ];

    const highPowerOffset = highPowerDataset.length;
    const newLapsByDatasetIndex: { [key: number]: MetricLap[] } = {};
    for (const [originalIndex, laps] of Object.entries(lapsByDatasetIndex)) {
      const adjustedIndex = parseInt(originalIndex) + highPowerOffset;
      newLapsByDatasetIndex[adjustedIndex] = laps;
    }

    return {
      datasets,
      lapsByDatasetIndex: newLapsByDatasetIndex,
      annotations: runBreakAnnotations,
      axisLimits: { xMin, xMax, yMin, yMax },
    };
  }, [metricLaps, plotSpecs, axisSpec]);

  // Get current zoom limits - use saved zoom if available, otherwise use calculated limits
  const getZoomLimits = () => {
    if (currentZoomRef.current) {
      return currentZoomRef.current;
    }
    return chartData.axisLimits;
  };

  const zoomLimits = getZoomLimits();

  const annotations = useMemo(() => {
    const result: Record<string, object> = {};
    (chartData.annotations || []).forEach((ann, i) => {
      const isDriver = ann.type === "driver";
      result[`line${i}`] = {
        type: "line",
        xMin: ann.x,
        xMax: ann.x,
        borderColor: isDriver ? ann.colour : "#7c7c7c",
        borderWidth: 3,
        borderDash: isDriver ? [] : [5, 5],
        label: {
          display: !!ann.label,
          content: ann.label,
          color: ann.labelColor,
          backgroundColor: "transparent",
          rotation: 90,
          position: "center",
          xAdjust: ann.xAdjust + (ann.smallFont ? -10 : 8),
          font: {
            size: ann.smallFont ? 10 : 12,
            weight: ann.smallFont ? "normal" : "bold",
          },
        },
      };
    });
    return result;
  }, [chartData.annotations]);

  const resetCurrentZoom = () => {
    const chart = chartRef.current;
    if (isLocked || !chart) {
      return;
    }

    // Reset to original axis limits
    currentZoomRef.current = null;
    const { xMin, xMax, yMin, yMax } = chartData.axisLimits;
    chart.options.scales = {
      ...chart.options.scales,
      x: {
        ...chart.options.scales?.x,
        min: xMin,
        max: xMax,
      },
      y: {
        ...chart.options.scales?.y,
        min: yMin,
        max: yMax,
      },
    };
    chart.update("none");
  };

  const chartOptions = {
    responsive: true,
    animation: { duration: 0 },
    maintainAspectRatio: false,
    plugins: {
      annotation: { annotations },
      legend: {
        labels: {
          color: "#000000ff",
          usePointStyle: true,
          boxWidth: 5,
          boxHeight: 5,
          generateLabels: (chart: ChartJS): LegendItem[] => {
            const datasets = chart.data.datasets;
            const uniqueLabels = new Map<string, LegendItem>();
            datasets.forEach((dataset, index) => {
              const lineDataset = dataset as ChartDataset<"line">;
              const label = dataset.label;
              if (label && !uniqueLabels.has(label)) {
                uniqueLabels.set(label, {
                  text: label,
                  strokeStyle:
                    (lineDataset.pointBorderColor as string) ||
                    (lineDataset.borderColor as string),
                  lineWidth: 1,
                  datasetIndex: index,
                  pointStyle: lineDataset.pointStyle as PointStyle,
                  fillStyle:
                    (lineDataset.pointBackgroundColor as string) ||
                    (lineDataset.borderColor as string),
                });
              }
            });
            return Array.from(uniqueLabels.values());
          },
        },
        onClick: () => {
          // Do nothing
        },
        position: "top" as const,
      },
      tooltip: {
        displayColors: true,
        callbacks: {
          title: () => "",
          label: (context: TooltipItem<"line">) => {
            const datasetLabel = context.dataset.label || "";
            const value = context.parsed.y;
            const roundedValue =
              typeof value === "number" ? value.toFixed(3) : value;
            return `${datasetLabel}: ${roundedValue}`;
          },
        },
      },
      zoom: {
        pan: {
          enabled: true,
          mode: "y" as const,
          onPanStart: () => !isLockedRef.current,
        },
        zoom: {
          wheel: {
            enabled: !isLocked,
          },
          pinch: {
            enabled: !isLocked,
          },
          mode: "y" as const,
          onZoom: ({ chart }: { chart: ChartJS }) => {
            // Save zoom state when user zooms
            currentZoomRef.current = {
              xMin: chart.scales.x.min,
              xMax: chart.scales.x.max,
              yMin: chart.scales.y.min,
              yMax: chart.scales.y.max,
            };
          },
        },
        onPanComplete: ({ chart }: { chart: ChartJS }) => {
          // Save zoom state when user pans
          currentZoomRef.current = {
            xMin: chart.scales.x.min,
            xMax: chart.scales.x.max,
            yMin: chart.scales.y.min,
            yMax: chart.scales.y.max,
          };
        },
      },
    },
    scales: {
      x: {
        type: "linear" as const,
        ticks: {
          display: false,
          stepSize: 1,
        },
        grid: {
          display: false,
        },
        min: zoomLimits.xMin,
        max: zoomLimits.xMax,
      },
      y: {
        type: "linear" as const,
        afterFit: function (scale: Scale) {
          scale.width = 60;
        },
        title: {
          display: true,
          text: `${axisSpec.YLabel} (${axisSpec.Units})`,
          color: "#000000ff",
        },
        ticks: {
          color: "#000000ff",
        },
        grid: {
          color: "rgba(0, 0, 0, 0.1)",
        },
        min: zoomLimits.yMin,
        max: zoomLimits.yMax,
      },
    },
    interaction: {
      mode: "point" as const,
      axis: "x" as const,
      intersect: false,
    },
    onClick: function (
      _: ChartEvent,
      elements: ActiveElement[],
      chart: ChartJS<"line">,
    ) {
      if (elements.length > 0) {
        const element = elements[0];
        const dataIndex = element.index;
        const datasetIndex = element.datasetIndex;
        const clickedDataset = chart.data.datasets[datasetIndex];
        const metricName = clickedDataset.label || "Unknown Metric";

        if (window.confirm(`Do you want to hide this ${metricName} point?`)) {
          const targetLap =
            chartData.lapsByDatasetIndex[datasetIndex]?.[dataIndex];
          if (targetLap) {
            // Save current zoom state before dispatching
            currentZoomRef.current = {
              xMin: chart.scales.x.min,
              xMax: chart.scales.x.max,
              yMin: chart.scales.y.min,
              yMax: chart.scales.y.max,
            };
            dispatch(
              disablePoint({
                description: targetLap.description || "",
                lapname: targetLap.lapname || "",
                metric: metricName,
              }),
            );
          }
        }
      }
    },
  };

  return (
    <div className={styles.container} onDoubleClick={resetCurrentZoom}>
      <div className={styles.controlsLeft}>
        <button
          className={styles.chartButton}
          onClick={() => {
            if (scrollTargetRef?.current) {
              scrollTargetRef.current.scrollIntoView({ behavior: "smooth" });
            }
          }}
          title="Scroll to top"
        >
          <FaHome />
        </button>
      </div>
      <div className={styles.controlsRight}>
        <button
          className={styles.chartButton}
          onClick={(_) => {
            setIsLocked((locked) => {
              const nextLocked = !locked;
              isLockedRef.current = nextLocked;
              // Reset zoom to default when locking
              if (nextLocked) {
                currentZoomRef.current = null;
                const chart = chartRef.current;
                if (chart) {
                  const { xMin, xMax, yMin, yMax } = chartData.axisLimits;
                  chart.options.scales = {
                    ...chart.options.scales,
                    x: {
                      ...chart.options.scales?.x,
                      min: xMin,
                      max: xMax,
                    },
                    y: {
                      ...chart.options.scales?.y,
                      min: yMin,
                      max: yMax,
                    },
                  };
                }
              }
              chartRef.current?.update("none");
              return nextLocked;
            });
          }}
        >
          {isLocked ? <FaLock /> : <FaUnlock />}
        </button>
        {(onMaximise || onMinimise) && (
          <button
            className={styles.chartButton}
            onClick={(_) => {
              if (isMaximised) {
                onMinimise?.();
              } else {
                onMaximise?.();
              }
            }}
          >
            {isMaximised ? <FaCompress /> : <FaExpand />}
          </button>
        )}
      </div>
      <Line
        ref={chartRef}
        data={{ datasets: chartData.datasets }}
        options={chartOptions}
      />
    </div>
  );
}

export default MetricRow;
